import React, { useMemo, useContext, useState, useEffect } from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import moment from 'moment';
import { toast } from 'react-toastify';
import TabPanel from '../../components/TabPanel';
// Context
import { DashboardProvider } from '../Dashboard/useDashboard';
import AppConst from '../../components/AppLayout/const';
import useEventCalendarContext from './context/useEventCalendarContext';
// Components
import Filter from './components/Filter';
import EventCalendarTab from './components/EventCalendarTab';
import AddEventModal from './components/AddEventModal';
import UpdateEventModal from './components/UpdateEventModal';
// API
import {
  postCalendar,
  postCalendarList,
  getEventTypeList,
  getViewCalendarEvent,
} from '../../api/eventCalendarApi';

export default function EventCalendar() {
  const defaultForm = {
    month_year: moment().format('YYYY-MM'),
  };

  const { onSetTitle } = useContext(AppConst.AppContext);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterForm, setFilterForm] = useState(defaultForm);
  const [calendarData, setCalendarData] = useState({
    loading: false,
    date: moment().format('YYYY-MM'),
    calendar: [],
  });
  const [calendarList, setCalendarList] = useState({ loading: false, list: [] });
  const addEventModalDefaultForm = {
    toggle: false,
    data: [
      {
        id: 1,
        date_from: moment().format('YYYY-MM-DD'),
        date_to: moment().format('YYYY-MM-DD'),
        name: '',
        type: '',
        event_type_selected: { value: null, label: 'Select Event' },
        is_plottable: true,
        is_enabled: 0,
      },
    ],
  };
  const [addEventModal, setAddEventModal] = useState(addEventModalDefaultForm);
  const [updateEventModal, setUpdateEventModal] = useState(addEventModalDefaultForm);
  const [eventTypeOptions, setEventTypeOptions] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedCalendarForm, setSelectedCalendarForm] = useState({
    date: null,
    hasSelected: false,
  });

  const handleEventTypeOptions = async () => {
    const response = await getEventTypeList();
    const { code, data } = response.data.response;
    if (code === '200') {
      const dropdownValue = data?.map((item) => ({
        label: item.name,
        value: item.id,
      }));

      setEventTypeOptions(dropdownValue);
    }
  };

  onSetTitle('Event Calendar');

  const handleChangeActiveTab = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleGenerateCalendarFilter = async (reset = null) => {
    try {
      toast.loading('Processing request please kindly wait...');
      setCalendarData({ ...calendarData, loading: true });
      const request = {
        date_from: moment(reset ? defaultForm?.month_year : filterForm?.month_year)
          .startOf('month')
          .format('YYYY-MM-DD'),
        date_to: moment(reset ? defaultForm?.month_year : filterForm?.month_year)
          .endOf('month')
          .format('YYYY-MM-DD'),
      };

      const response = await postCalendar(request);
      const { code, message, data } = response.data.response;
      if (code === '200') {
        toast.success(message);
        setCalendarData({
          ...calendarData,
          loading: false,
          date: moment(request.date_from).format('YYYY-MM'),
          calendar: data,
        });
      }
      toast.dismiss();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleGenerateListFilter = async (calendarForm = null, reset = null) => {
    try {
      toast.loading('Processing request please kindly wait...');
      setCalendarList({ ...calendarList, loading: true });
      let request;

      if (calendarForm?.hasSelected) {
        request = {
          date_from: moment(calendarForm?.date).format('YYYY-MM-DD'),
          date_to: moment(calendarForm?.date).format('YYYY-MM-DD'),
        };
      } else {
        request = {
          date_from: moment(reset ? defaultForm?.month_year : filterForm?.month_year)
            .startOf('month')
            .format('YYYY-MM-DD'),
          date_to: moment(reset ? defaultForm?.month_year : filterForm?.month_year)
            .endOf('month')
            .format('YYYY-MM-DD'),
        };
      }

      const response = await postCalendarList(request);
      const { code, message, data } = response.data.response;
      if (code === '200') {
        toast.success(message);
        setCalendarList({ ...calendarList, loading: false, list: data });
      }
      toast.dismiss();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleGenerateFilter = () => {
    handleGenerateCalendarFilter();
    handleGenerateListFilter();
  };

  const handleClearFilter = () => {
    setFilterForm(defaultForm);
    handleGenerateCalendarFilter(true);
    handleGenerateListFilter(null, true);
  };

  const handleViewEvent = async (id) => {
    try {
      const request = {
        datas: [id],
      };
      const response = await getViewCalendarEvent(request);
      const { code, message, data } = response.data.response;
      if (code === '200') {
        toast.success(message);
        setUpdateEventModal({
          ...updateEventModal,
          toggle: true,
          data: data.map((row) => ({
            id: row.id,
            date_from: row.date,
            date_to: row.date,
            name: row.name,
            type: row.type,
            event_type_id: row.type_id,
            event_type_selected: eventTypeOptions.find((item) => item.value === row.type_id),
            is_plottable: row.is_plottable,
          })),
        });
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  useEffect(() => {
    handleGenerateFilter();
    handleEventTypeOptions();
  }, []);
  return (
    <DashboardProvider>
      <useEventCalendarContext.Provider
        value={useMemo(
          () => ({
            // GET STATE
            loading,
            filterForm,
            calendarData,
            calendarList,
            addEventModalDefaultForm,
            addEventModal,
            updateEventModal,
            eventTypeOptions,
            selectedDate,
            selectedCalendarForm,
            // SET STATE
            setFilterForm,
            setLoading,
            setCalendarData,
            setCalendarList,
            setAddEventModal,
            setUpdateEventModal,
            setSelectedDate,
            setSelectedCalendarForm,
            // FUNCTIONS
            handleGenerateFilter,
            handleGenerateCalendarFilter,
            handleGenerateListFilter,
            handleClearFilter,
            handleViewEvent,
          }),
          [
            loading,
            filterForm,
            calendarData,
            calendarList,
            selectedCalendarForm,
            addEventModal.toggle,
            addEventModal.data,
            updateEventModal.toggle,
            updateEventModal.data,
          ],
        )}
      >
        <Box sx={{ backgroundColor: '#fff', p: 1, boxShadow: 3 }}>
          <Filter />
        </Box>
        <Box padding={4}>
          <Grid container spacing={2}>
            <Grid xs={12} sm={12} md={12}>
              <Box sx={{ backgroundColor: '#fff' }} mt={4}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs
                    value={activeTab}
                    onChange={handleChangeActiveTab}
                    aria-label='basic tabs example'
                  >
                    <Tab
                      label='Event Calendar'
                      id='event-calendar-tab'
                      aria-controls='event-calendar-tab'
                    />
                  </Tabs>
                </Box>
                <TabPanel value={activeTab} index={activeTab}>
                  <EventCalendarTab />
                </TabPanel>
              </Box>
            </Grid>
          </Grid>
        </Box>
        <AddEventModal />
        <UpdateEventModal />
      </useEventCalendarContext.Provider>
    </DashboardProvider>
  );
}
